import React, {useEffect, useState} from 'react';

import {colors} from 'js-libs/sweetcommons/styles';
import styled from 'styled-components';

import StateButton from 'app/general/widgets/StateButton';
import {useCreateSuggestions, useSuggestion} from 'app/openai/api';
import {useAccountSettingsState} from 'app/settings/api';
import {getContentType} from 'app/utils/content-type';
import {gt} from 'app/utils/i18n';

import {PermitOpenaiDataGathering} from './PermitOpenaiDataGathering';

import {extractSuggestionsOfType} from '../utils';

const DEFAULT_SUGGESTED_TITLES = [
  'Secure Payment Processing',
  'Product Delivery Tracking',
  'Customer Service Response',
];

type Props = {
  contentTypePlural: 'procedures' | 'policies';
  contentType: 'procedure' | 'policy';
  onClick: (string) => void;
};
export const TitleSuggestions = ({
  contentType,
  contentTypePlural,
  onClick,
}: Props) => {
  const {industry_classification} = useAccountSettingsState().data!;
  const [createSuggestions] = useCreateSuggestions();
  const [suggestionURL, setSuggestionURL] = useState<string | undefined>(
    undefined
  );
  const {data: suggestion} = useSuggestion(suggestionURL ?? '', {
    skip: suggestionURL == null,
    refetchOnMountOrArgChange: false,
  });

  const [titles, setTitles] = useState<Array<string>>(DEFAULT_SUGGESTED_TITLES);

  const [businessCategory, setBusinessCategory] = useState<string>(
    industry_classification || 'online shopping'
  );

  useEffect(() => {
    if (suggestion != null) {
      setTitles(extractSuggestionsOfType(suggestion.results, contentType));
    }
  }, [suggestion]);

  const doCreateSuggestions = (prompt) => {
    return createSuggestions({prompt, content_types: [contentType]})
      .unwrap()
      .then(({result}) => setSuggestionURL(result.url));
  };

  return (
    <>
      <div className="row">
        <div className="col">
          <div className="lead">
            <PermitOpenaiDataGathering />

            <div className="form-inline">
              <label htmlFor="category" className="mr-2">
                {gt('Suggest %(contentTypePlural)s for a business in', {
                  contentTypePlural,
                })}
              </label>
              <StyledInput
                id="category"
                className="pl-1 mr-2"
                type="text"
                value={businessCategory}
                onChange={(e) => setBusinessCategory(e.target.value)}
              />
              <StateButton
                disabled={businessCategory.length === 0}
                busyText={gt('Suggesting some titles...')}
                className="btn-link"
                onClick={() => doCreateSuggestions(businessCategory)}
              >
                <i className="fa fa-redo mr-1" />
                {gt('Reload suggestions')}
              </StateButton>
            </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <ul className="list-group mt-4">
            {titles.map((doc, i) => (
              <DocItem
                key={`${doc}-${i}`}
                doc={doc}
                content_type={contentType}
                onClick={onClick}
              />
            ))}
          </ul>
        </div>
      </div>
    </>
  );
};

type SelectProps = {
  doc: string;
  content_type: 'procedure' | 'policy';
  onClick: (string) => void;
};

const DocItem = ({doc, content_type, onClick}: SelectProps) => {
  const ct = getContentType(content_type);

  return (
    <li className="list-group-item list-group-item-action flex-column align-items-start">
      <div
        className="d-flex w-100 justify-content-between"
        onClick={() => onClick(doc)}
        role="button"
      >
        <h5>
          <i className={`mr-1 ${ct.icon}`} />
          {doc}
        </h5>
      </div>
    </li>
  );
};

const StyledInput = styled.input`
  background: transparent;
  border: none;
  border-bottom: 2px solid ${colors.textDefault};
  outline: none;
  &:focus {
    border-bottom: 2px solid black;
    outline: none;
  }
`;
